import React, { createContext, useContext, useState } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import supabase from '../supabase/supabase';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { currentTenant } = useCRM();

  const plans = [
    { id: 'start', name: 'Start', price: 599, userLimit: 5, storage: '1GB', features: ['CRM', 'Pipeline'] },
    { id: 'pro', name: 'Vekst', price: 990, userLimit: 50, storage: '10GB', features: ['CRM', 'Pipeline', 'Automasjon', 'Email Copilot'] },
    { id: 'enterprise', name: 'Enterprise', price: 2990, userLimit: 999, storage: 'Unlimited', features: ['Alt i Pro', 'SSO', 'Audit Logs', 'Dedikert Support'] }
  ];

  const [demoBookings, setDemoBookings] = useState([
    { id: 'db-1', company: 'Acme Corp', contact: 'Ola Nordmann', email: 'ola@acme.no', phone: '99887766', date: '2024-05-15', time: '10:00', status: 'Ny', assignedTo: null }
  ]);
  const [pendingPartners, setPendingPartners] = useState([]);
  const [partners, setPartners] = useState([
    { id: 'part-123', name: 'Demo Partner AS', commissionRate: 0.2, customers: [{ name: 'Kunde A', plan: 'Vekst', signupDate: '2024-01-10', mrr: 990 }] }
  ]);
  const [sellers, setSellers] = useState([
    { id: 'sel-456', name: 'Demo Selger', customers: [{ name: 'Kunde B', plan: 'Enterprise', signupDate: '2024-02-15', mrr: 2990 }] }
  ]);
  const [campaigns, setCampaigns] = useState([]);
  const [portalResources, setPortalResources] = useState([
    { id: 'res-1', title: 'Salgspitch 2024 (PDF)', type: 'PDF', audience: 'all', description: 'Standard presentasjon for nye kunder.' }
  ]);
  const [invoices, setInvoices] = useState([
    { id: 'inv-1', date: new Date().toISOString(), amount: 990, status: 'Betalt' }
  ]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountCodesLoading, setDiscountCodesLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const systemMetrics = {
    mrr: [{ date: 'Jan', value: 120000 }, { date: 'Jun', value: 198000 }],
    activeUsers: 1245,
    tenants: 89
  };

  const loadDiscountCodes = async () => {
    setDiscountCodesLoading(true);

    const { data, error } = await supabase
      .from('platform_discount_codes_1788703000000')
      .select('*')
      .order('created_at', { ascending: false });

    setDiscountCodesLoading(false);

    if (error) {
      return { success: false, error };
    }

    setDiscountCodes(data || []);
    return { success: true, data: data || [] };
  };

  const createDiscountCode = async (discount) => {
    const { data: userData } = await supabase.auth.getUser();
    const payload = {
      name: discount.name.trim(),
      code: discount.code.trim().toUpperCase(),
      percent_off: Number(discount.percent_off || 0),
      amount_off: Number(discount.amount_off || 0),
      plan_id: discount.plan_id || '',
      starts_at: discount.starts_at || null,
      ends_at: discount.ends_at || null,
      duration_days: discount.duration_days ? Number(discount.duration_days) : null,
      is_active: Boolean(discount.is_active),
      created_by: userData?.user?.id || null
    };

    const { data, error } = await supabase
      .from('platform_discount_codes_1788703000000')
      .insert(payload)
      .select()
      .single();

    if (error) {
      return { success: false, error };
    }

    setDiscountCodes(current => [data, ...current]);
    return { success: true, data };
  };

  const updateDiscountCode = async (id, changes) => {
    const payload = {
      ...changes,
      code: changes.code ? changes.code.trim().toUpperCase() : undefined,
      updated_at: new Date().toISOString()
    };

    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) delete payload[key];
    });

    const { data, error } = await supabase
      .from('platform_discount_codes_1788703000000')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { success: false, error };
    }

    setDiscountCodes(current => current.map(item => item.id === id ? data : item));
    return { success: true, data };
  };

  const addDemoBooking = (data) => {
    setDemoBookings(prev => [{ id: generateId('db'), status: 'Ny', assignedTo: null, ...data }, ...prev]);
  };

  const updateDemoBooking = (id, updates) => {
    setDemoBookings(prev => prev.map(db => db.id === id ? { ...db, ...updates } : db));
  };

  const addPortalResource = (resource) => {
    setPortalResources(prev => [{ id: generateId('res'), ...resource }, ...prev]);
  };

  const deletePortalResource = (id) => {
    setPortalResources(prev => prev.filter(r => r.id !== id));
  };

  const upgradePlan = async (planId, paymentData) => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    setLoading(false);
  };

  const cancelSubscription = () => {
    alert('Abonnement avsluttet');
  };

  const downloadInvoice = (id) => {
    alert('Laster ned faktura ' + id);
  };

  const submitSupportTicket = (subject, message, priority) => {
    return 'TKT-' + Math.floor(Math.random() * 10000);
  };

  const getCurrentPlan = () => plans.find(p => p.id === (currentTenant?.planId || 'start')) || plans[0];

  return (
    <SubscriptionContext.Provider value={{
      plans, campaigns,
      partners, sellers, pendingPartners, currentPlan: getCurrentPlan(), systemMetrics,
      demoBookings, addDemoBooking, updateDemoBooking,
      portalResources, addPortalResource, deletePortalResource,
      discountCodes, discountCodesLoading, loadDiscountCodes, createDiscountCode, updateDiscountCode,
      invoices, loading, upgradePlan, cancelSubscription, downloadInvoice, submitSupportTicket,
      addCampaign: () => {}, approvePartner: () => {}, rejectPartner: () => {}
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};